import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapView from './components/MapView.jsx';
import ReportPanel from './components/ReportPanel.jsx';
import ReportComposer from './components/ReportComposer.jsx';
import Leaderboard from './components/Leaderboard.jsx';
import AccountSheet from './components/AccountSheet.jsx';
import {
  fetchTaxonomy, fetchReportsInBounds, fetchNearbyReports, fetchReport, fetchConfig,
  isConfigured,
} from './lib/db.js';
import { getSession, onAuthChange, cleanAuthParamsFromUrl } from './lib/auth.js';
import { useGeolocation, searchPlace } from './lib/useGeolocation.js';
import { getLastLocation, setLastLocation } from './lib/last-location.js';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { I18nProvider, useLocale, changeLocale, matchLocale, hasLocalePreference, FALLBACK_LOCALE } from './lib/i18n.js';
import { localizeTaxonomy } from './lib/taxonomy-i18n.js';

const DAY_OPTIONS = [1, 3, 7, 14, 30];

// The viewport is bracketed by occluders — the topbar above and, when a
// report is open, the composer sheet below. flyTo carries their heights
// (CSS px); MapView centres the target at the midpoint of the visible band.
const TOP_PAD_PX = 64;    // .topbar footprint (cf. .leaflet-control-zoom margin)
const SHEET_PAD_PX = 320; // bottom composer sheet

function AppInner() {
  const { t } = useTranslation();
  const locale = useLocale();
  const mapRef = useRef(null);
  const didInitialCentre = useRef(false);

  const [taxonomy, setTaxonomy] = useState(null);
  const [config, setConfig] = useState({});
  const [reports, setReports] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [daysBack, setDaysBack] = useState(7);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [capHit, setCapHit] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedMissing, setSelectedMissing] = useState(false);

  const [sheet, setSheet] = useState(null);          // 'report' | 'leaderboard' | 'account'
  const [draftPin, setDraftPin] = useState(null);
  const [pinDrop, setPinDrop] = useState(null);      // pending tap-to-place pin
  const [flyTo, setFlyTo] = useState(null);
  const [user, setUser] = useState(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchNudge, setSearchNudge] = useState(false);
  const [createdIds, setCreatedIds] = useState(() => new Set());

  const geo = useGeolocation({ auto: true });
  // Read once: localStorage doesn't change mid-session, and the map only uses
  // the initial centre on first mount.
  const [lastLoc] = useState(getLastLocation);

  // --- one-time setup -------------------------------------------------------
  useEffect(() => {
    cleanAuthParamsFromUrl();
    getSession().then((session) => setUser(session?.user ?? null));
    return onAuthChange(setUser);
  }, []);

  useEffect(() => {
    if (!isConfigured) return;
    fetchTaxonomy()
      .then(setTaxonomy)
      .catch((err) => setLoadError(err.message));
    // A failure here is not worth surfacing: the defaults are sane and the
    // map still works.
    fetchConfig().then(setConfig).catch(() => {});
  }, []);

  // Apply the deployment's default locale once known, but only when the user
  // expressed no explicit preference (storage choice or a browser match).
  useEffect(() => {
    const def = config?.default_locale;
    if (!def || hasLocalePreference()) return;
    const target = matchLocale(def) ?? FALLBACK_LOCALE;
    if (target !== locale) changeLocale(target);
  }, [config?.default_locale, locale]);

  // Localize the taxonomy labels for the active locale (Approach A: overlay by
  // slug with en-GB fallback). Picker, composer and markers all read this.
  const localizedTaxonomy = useMemo(
    () => (taxonomy ? localizeTaxonomy(taxonomy, locale) : taxonomy),
    [taxonomy, locale],
  );

  // Deep link from a generated landing page: /?category=<slug> pre-filters
  // the map so a visitor from /categories/pothole/ lands on a filtered view.
  useEffect(() => {
    if (!localizedTaxonomy) return;
    const slug = new URLSearchParams(window.location.search).get('category');
    if (!slug) return;
    for (const col of localizedTaxonomy) {
      const cat = col.categories.find((c) => c.slug === slug);
      if (cat) {
        setCategoryFilter(cat);
        return;
      }
    }
  }, [localizedTaxonomy]);

  // Reflect the active filter (or lack of one) in the document title so a
  // filtered view tells users and crawlers what they are looking at.
  useEffect(() => {
    document.title = categoryFilter
      ? t('app.titleFiltered', { category: categoryFilter.name })
      : t('app.titleDefault');
  }, [categoryFilter, t]);

  // Individual report deep links (?report=<uuid>) are ephemeral and decay
  // out from under the index — keep them out of Google's index but still
  // allow following links out of a shared report.
  useEffect(() => {
    const hasReport = new URLSearchParams(window.location.search).has('report');
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', hasReport ? 'noindex, follow' : 'index, follow');
  }, [selectedId]);

  // Cold-load deep link (plan.md §4.3). Resolved via get_report, which ignores
  // expiry, viewport and the history window — so a shared link works days
  // later, from anywhere, whatever the current filter says.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('report');
    if (id) openReport(id, { fly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Centre on the user once geolocation resolves, unless a deep link already
  // decided where to look.
  useEffect(() => {
    if (!geo.position || new URLSearchParams(window.location.search).get('report')) return;

    const isFirst = !didInitialCentre.current;
    didInitialCentre.current = true;

    setFlyTo({
      lat: geo.position.lat,
      lng: geo.position.lng,
      ...(isFirst ? { zoom: lastLoc?.zoom ?? 15, instant: true } : {}),
    });

    const zoom = mapRef.current?.getZoom() ?? lastLoc?.zoom ?? 15;
    setLastLocation({ lat: geo.position.lat, lng: geo.position.lng, zoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo.position]);

  useEffect(() => {
    if (geo.status === 'denied' || geo.status === 'unavailable') setSearchNudge(true);
  }, [geo.status]);

  // --- the map read path ----------------------------------------------------
  const refresh = useCallback(async () => {
    if (!bounds || !isConfigured) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchReportsInBounds({
        bounds, daysBack, categoryId: categoryFilter?.id ?? null,
      });
      setReports(rows);
      // The function caps results server-side; hitting the cap means the area
      // is denser than the map can honestly show (plan.md §3, §4.1).
      setCapHit(rows.length >= Number(config.map_result_cap ?? 500));
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bounds, daysBack, categoryFilter, config.map_result_cap]);

  useEffect(() => { refresh(); }, [refresh]);

  // Debounce the spinner: only reveal it if a load is still in flight after
  // 250ms, so fast cached viewport loads don't blink the indicator.
  useEffect(() => {
    if (!loading) { setShowSpinner(false); return; }
    const t = setTimeout(() => setShowSpinner(true), 250);
    return () => clearTimeout(t);
  }, [loading]);

  // The initial "what's near me" load, before a viewport is meaningful.
  useEffect(() => {
    if (!geo.position || bounds || !isConfigured) return;
    fetchNearbyReports({ lat: geo.position.lat, lng: geo.position.lng, daysBack })
      .then(setReports)
      .catch(() => {});
  }, [geo.position, bounds, daysBack]);

  // --- selection & URL sync (plan.md §4.3) ----------------------------------
  const syncUrl = useCallback((id) => {
    const url = new URL(window.location.href);
    if (id) url.searchParams.set('report', id);
    else url.searchParams.delete('report');
    // replaceState, so the address bar is always a valid deep link without
    // filling the back stack with every marker tap.
    window.history.replaceState({}, '', url.toString());
  }, []);

  async function openReport(id, { fly = false } = {}) {
    setSelectedId(id);
    setSelectedMissing(false);
    setSheet(null);
    setPinDrop(null);
    syncUrl(id);

    // Render immediately from the map data if we already have it.
    const known = reports.find((r) => r.id === id);
    if (known) setSelected(known);
    else setSelectedLoading(true);

    try {
      const full = await fetchReport(id);
      if (!full) {
        // Purged by retention, or never existed (plan.md §7).
        setSelected(null);
        setSelectedMissing(true);
        return;
      }
      setSelected(full);
      if (fly || !known) {
        setFlyTo({ lat: full.lat, lng: full.lng, zoom: 16, padTop: TOP_PAD_PX, padBottom: SHEET_PAD_PX });
      }
    } catch {
      if (!known) setSelectedMissing(true);
    } finally {
      setSelectedLoading(false);
    }
  }

  function closeReport() {
    setSelectedId(null);
    setSelected(null);
    setSelectedMissing(false);
    syncUrl(null);
  }

  function handleSelectReport(id) {
    if (!id) closeReport();
    else openReport(id);
  }

  // --- reporting ------------------------------------------------------------
  // Two-stage placement (plan.md §4.2, mobile UX).  Tapping the map drops a
  // pending pin with a "Report here" popup; tapping that promotes the pin to
  // a draft and opens the composer.  Dragging the map still pans, because
  // Leaflet distinguishes click from drag natively.
  function handleMapClick({ lat, lng }) {
    // A map tap is intent to interact with the map, so dismiss the place-search
    // panel — otherwise it occludes the pin we drop on this same tap.
    setSearchOpen(false);
    // While the composer is open, MapView routes clicks through onDraftMove,
    // so we should never get here — but stay defensive.
    if (sheet === 'report') return;
    // First tap on the map with a panel open just dismisses the panel; the
    // second tap drops the pin.  Same for any side-sheet.
    if (selectedId || selectedMissing) { closeReport(); return; }
    if (sheet) { setSheet(null); return; }
    setPinDrop({ lat, lng });
  }

  function confirmPinDrop() {
    if (!pinDrop) return;
    const origin = pinDrop;
    setDraftPin({ ...origin, precisionM: 0 });
    setPinDrop(null);
    setSheet('report');
    setFlyTo({ lat: origin.lat, lng: origin.lng, zoom: 17, padTop: TOP_PAD_PX, padBottom: SHEET_PAD_PX });
  }

  function cancelPinDrop() {
    setPinDrop(null);
  }

  function endReport() {
    setDraftPin(null);
    setSheet(null);
  }

  async function handleCreated(result) {
    setCreatedIds((s) => new Set(s).add(result.report_id));
    endReport();
    await refresh();
    // Centre on the coordinate that was actually STORED, which for a snapped
    // collection is not where the pin was — showing the true position would
    // misrepresent what got published (plan.md §4.2).
    setFlyTo({ lat: result.lat, lng: result.lng, zoom: 16, padTop: TOP_PAD_PX, padBottom: SHEET_PAD_PX });
    openReport(result.report_id);
  }

  async function handlePlaceSearch(event) {
    event.preventDefault();
    if (!placeQuery.trim()) return;
    setPlaceResults('loading');
    try {
      setPlaceResults(await searchPlace(placeQuery));
    } catch {
      setPlaceResults([]);
    }
  }

  function goToPlace(place) {
    setFlyTo({ lat: place.lat, lng: place.lng, zoom: 14, padTop: TOP_PAD_PX });
    setLastLocation({ lat: place.lat, lng: place.lng, zoom: 14, label: place.label });
    setSearchOpen(false);
    setPlaceResults(null);
    setPlaceQuery('');
  }

  const mapCentre = useMemo(() => {
    if (geo.position) return geo.position;
    const centre = mapRef.current?.getCenter();
    return centre ? { lat: centre.lat, lng: centre.lng } : (lastLoc ?? geo.defaultCentre);
  }, [geo.position, geo.defaultCentre, bounds, lastLoc]);

  if (!isConfigured) {
    return (
      <div className="setup">
        <h1>{t('app.setupTitle')}</h1>
        <p>
          <Trans i18nKey="app.setupBody" components={[<code />, <code />, <code />]} />
        </p>
      </div>
    );
  }

  return (
    <div className="app">
      <MapView
        mapRef={mapRef}
        reports={reports}
        selectedId={selectedId}
        onSelectReport={handleSelectReport}
        onBoundsChange={setBounds}
        initialCenter={geo.position ?? lastLoc ?? geo.defaultCentre}
        initialZoom={lastLoc?.zoom ?? 14}
        flyTo={flyTo}
        draftPin={draftPin}
        pinDrop={pinDrop}
        onPinDropMove={(next) => setPinDrop((prev) => (prev ? { ...prev, ...next } : prev))}
        onConfirmPinDrop={confirmPinDrop}
        onCancelPinDrop={cancelPinDrop}
        onMapClick={handleMapClick}
        // Merge rather than replace: the map only knows the new coordinate,
        // and clobbering the object would drop the precision the composer set
        // when a category was chosen, taking the halo with it.
        onDraftMove={sheet === 'report'
          ? (position) => setDraftPin((prev) => ({ ...prev, ...position }))
          : null}
      />

      <header className="topbar">
        <div className="topbar__filters">
          <label className="select">
            <span className="select__label">{t('app.filtersLast')}</span>
            <select
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              aria-label={t('app.ariaLastDays')}
            >
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>{t('units.day', { count: d })}</option>
              ))}
            </select>
          </label>

          {categoryFilter && (
            <button
              type="button"
              className="chip"
              onClick={() => setCategoryFilter(null)}
            >
              {categoryFilter.name} <span aria-hidden="true">×</span>
              <span className="sr-only">{t('app.ariaRemoveFilter')}</span>
            </button>
          )}

          <span
            className="spinner"
            role="status"
            aria-label={t('app.ariaLoading')}
            hidden={!showSpinner}
          />
        </div>

        <div className="topbar__actions">
          <button
            type="button"
            className={`btn btn--chip${searchNudge ? ' btn--nudge' : ''}`}
            onClick={() => setSearchOpen((v) => !v)}
            aria-label={t('app.ariaSearchPlace')}
            aria-pressed={searchOpen}
            onAnimationEnd={() => setSearchNudge(false)}
          >
            🔍
          </button>
          <button
            type="button"
            className="btn btn--chip"
            onClick={() => geo.locate()}
            disabled={geo.status === 'locating'}
            aria-label={t('app.ariaCentre')}
          >
            📍
          </button>
          <button
            type="button"
            className="btn btn--chip"
            onClick={() => { setPinDrop(null); setSheet(sheet === 'leaderboard' ? null : 'leaderboard'); }}
            aria-label={t('app.ariaNearby')}
            aria-pressed={sheet === 'leaderboard'}
          >
            🧭
          </button>
          <button
            type="button"
            className="btn btn--chip"
            onClick={() => { setPinDrop(null); setSheet(sheet === 'account' ? null : 'account'); }}
            aria-label={user ? t('app.ariaAccount') : t('app.ariaMine')}
            aria-pressed={sheet === 'account'}
          >
            👤
          </button>
        </div>
      </header>

      {(capHit || loadError || geo.status === 'locating') && (
        <div className="statusbar" role="status">
          {loadError && <span className="statusbar__error">{loadError}</span>}
          {!loadError && capHit && <span>{t('app.capHit')}</span>}
          {!loadError && !capHit && geo.status === 'locating' && (
            <span>{t('app.locating')}</span>
          )}
        </div>
      )}

      {searchOpen && (
        <div className="location-help">
          {(geo.status === 'denied' || geo.status === 'unavailable') && (
            <>
              <p className="location-help__text">
                {geo.status === 'denied'
                  ? (lastLoc ? t('app.geoOffLast') : t('app.geoOffDefault'))
                  : (lastLoc ? t('app.geoFailLast') : t('app.geoFail'))}
              </p>
              <button
                type="button"
                className="btn btn--chip location-help__retry"
                onClick={() => geo.locate()}
              >
                {t('app.retry')}
              </button>
            </>
          )}
          <form className="location-help__form" onSubmit={handlePlaceSearch}>
            <input
              type="search"
              value={placeQuery}
              onChange={(e) => setPlaceQuery(e.target.value)}
              placeholder={t('app.searchPlaceholder')}
              aria-label={t('app.ariaSearchPlace')}
              autoFocus
            />
            <button type="submit" className="btn btn--primary btn--small">{t('app.searchGo')}</button>
            <button
              type="button"
              className="btn btn--icon"
              onClick={() => setSearchOpen(false)}
              aria-label={t('app.searchDismiss')}
            >×</button>
          </form>
          {placeResults === 'loading' && <p className="hint hint--small">{t('app.searchSearching')}</p>}
          {Array.isArray(placeResults) && placeResults.length === 0 && (
            <p className="hint hint--small">{t('app.searchNoPlaces')}</p>
          )}
          {Array.isArray(placeResults) && placeResults.map((place) => (
            <button
              key={`${place.lat},${place.lng}`}
              type="button"
              className="location-help__result"
              onClick={() => goToPlace(place)}
            >
              {place.label}
            </button>
          ))}
        </div>
      )}

      {(selectedId || selectedMissing) && (
        <ReportPanel
          report={selected}
          loading={selectedLoading}
          notFound={selectedMissing}
          isOwn={selectedId != null && createdIds.has(selectedId)}
          position={geo.position}
          ensureAccurateFix={geo.ensureAccurateFix}
          corroborationMaxAccuracyM={Number(config.corroboration_max_accuracy_m ?? 50)}
          corroborationRangeM={Number(config.corroboration_range_m ?? 1000)}
          onClose={closeReport}
          onUpdated={(updated) => {
            setSelected(updated);
            setReports((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
          }}
        />
      )}

      {sheet === 'report' && localizedTaxonomy && (
        <ReportComposer
          taxonomy={localizedTaxonomy}
          draftPin={draftPin}
          emergencyNumber={config.emergency_number}
          onDraftMove={(next) => setDraftPin((prev) => ({ ...prev, ...next }))}
          onClose={endReport}
          onCreated={handleCreated}
          onShowExisting={(id) => { endReport(); openReport(id, { fly: true }); }}
        />
      )}

      {sheet === 'leaderboard' && (
        <Leaderboard
          centre={mapCentre}
          daysBack={daysBack}
          onClose={() => setSheet(null)}
          onPickCategory={(category) => {
            setCategoryFilter(category);
            setSheet(null);
          }}
        />
      )}

      {sheet === 'account' && (
        <AccountSheet
          user={user}
          onClose={() => setSheet(null)}
          onOpenReport={(id) => { setSheet(null); openReport(id, { fly: true }); }}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AppInner />
    </I18nProvider>
  );
}
